import React, {useEffect, useMemo, useState} from 'react';
import {Box, Checkbox, Chip, IconButton, MenuItem, Stack, styled, TextField, Tooltip, Typography} from '@mui/material';
import {useLocation, useNavigate, useOutletContext} from 'react-router-dom';
import moment from 'moment/moment';
import {formatDate, formatFormExpirationDate} from '../../../utility/dateUtil';
import FormExpirationDatePicker from './FormExpirationDatePicker';
import {validateFieldLength} from '../../../utility/validationUtil';
import ContentPasteOffRoundedIcon from '@mui/icons-material/ContentPasteOffRounded';
import {
    useArchiveFormMutation,
    useBulkArchiveFormsMutation,
    useRestoreFormMutation,
    useCloneFormMutation,
    useBulkCloneFormsMutation,
    useCreateFormTemplateMutation,
    useDeleteFormMutation,
    useDeleteSchoolTemplateMutation,
    useGetFormsQuery,
    useGetSchoolTemplatesQuery,
    useUpdateFormDetailsMutation,
    useUpdateFormStatusMutation,
    useUpdateSchoolTemplateMutation
} from '../../../api/services/formsService';
import CreateForm from './FormCreation/CreateForm';
import {FORM_STATUSES, FORM_STATUSES_NAMES} from '../../../constants/formStatuses';
import useNotification from '../../../utility/hooks/useNotification';
import AdmicityTableV2 from '../../../shared-components/Table/V2/AdmicityTableV2';
import FILTER_OPERATIONS from '../../../constants/filterOperations';
import withPaginationV2 from '../../../hoc/pagination/withPaginationV2';
import {FORM_ACCESS_LEVELS, FORM_ACCESS_LEVELS_NAMES} from '../../../constants/formAccessLevels';
import DateRangePicker from '../../../shared-components/Table/V2/components/columnFilters/DateRangePicker';
import CircleIcon from '@mui/icons-material/Circle';
import {useGetFormCategoriesQuery} from '../../../api/services/formCategoriesService';
import {useConfirm} from 'material-ui-confirm';
import {getFormUrl} from './PublicFormSettings';
import AdmicityDialog from '../../../shared-components/AdmicityDialog';
import useTextField from '../../../utility/hooks/useTextField';
import AdmicityTabs from '../../../shared-components/AdmicityTabs';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const expirationHeader = (
    <Box sx={{
        display: 'inline-flex',
        gap: 0.5
    }}>
        <span>Expiration Date</span>
        <Tooltip title="Expiration date and time are shown in your local time">
            <IconButton
                size="small"
                sx={{p: 0.25}}
                tabIndex={-1}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
            >
                <InfoOutlinedIcon fontSize="inherit"/>
            </IconButton>
        </Tooltip>
    </Box>
);

/* eslint-disable react/prop-types */

const StyledCell = styled('div')({
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxHeight: '24px',
    width: '350px'
});

export const CustomCell = ({cell}) => {
    const value = cell.getValue();
    return (
        <Tooltip title={value} arrow>
            <StyledCell>
                {value}
            </StyledCell>
        </Tooltip>
    );
};

const Table = withPaginationV2(AdmicityTableV2, {func: useGetFormsQuery});
const SchoolTemplatesTable = withPaginationV2(AdmicityTableV2, {func: useGetSchoolTemplatesQuery});
const TEMPLATE_NAME_MAX_LENGTH = 500;

const validateTemplateName = (value) => {
    const trimmedValue = value?.trim() ?? '';

    if (!trimmedValue) {
        return {
            isValid: false,
            message: 'Template name is required'
        };
    }

    if (trimmedValue.length > TEMPLATE_NAME_MAX_LENGTH) {
        return {
            isValid: false,
            message: `Template name must be ${TEMPLATE_NAME_MAX_LENGTH} characters or fewer`
        };
    }

    return {isValid: true};
};

const Forms = () => {
    const [updateFormStatus] = useUpdateFormStatusMutation();
    const [deleteForm] = useDeleteFormMutation();
    const [updateFormDetails] = useUpdateFormDetailsMutation();
    const [cloneForm] = useCloneFormMutation();
    const [archiveForm] = useArchiveFormMutation();
    const [bulkCloneForms] = useBulkCloneFormsMutation();
    const [bulkArchiveForms] = useBulkArchiveFormsMutation();
    const [restoreForm] = useRestoreFormMutation();
    const [createFormTemplate, createFormTemplateResult] = useCreateFormTemplateMutation();
    const {data: formCategories} = useGetFormCategoriesQuery();
    const navigate = useNavigate();
    const location = useLocation();
    const {
        showErrorNotification,
        showInfoNotification,
        showDetailedErrorNotification
    } = useNotification();
    const {key} = useOutletContext();
    const confirm = useConfirm();
    const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
    const [selectedFormForTemplate, setSelectedFormForTemplate] = useState(null);
    const templateNameField = useTextField({
        initialValue: '',
        required: true,
        validate: validateTemplateName
    });
    const [editTemplateDialogOpen, setEditTemplateDialogOpen] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const editTemplateNameField = useTextField({
        initialValue: '',
        required: true,
        validate: validateTemplateName
    });
    const [updateSchoolTemplate] = useUpdateSchoolTemplateMutation();
    const [deleteSchoolTemplate] = useDeleteSchoolTemplateMutation();
    const [activeTabIndex, setActiveTabIndex] = useState(() => {
        const indexFromState = location.state?.activeTabIndex;
        return typeof indexFromState === 'number' ? indexFromState : 1;
    });
    const [formDetailsDialogOpen, setFormDetailsDialogOpen] = useState(false);
    const [selectedFormForDetails, setSelectedFormForDetails] = useState(null);
    const [formDetailsValues, setFormDetailsValues] = useState({
        name: '',
        categoryIds: [],
        expirationDate: null,
        remindInterval: '0'
    });
    const formNameValidation = validateFieldLength(formDetailsValues.name, 128);
    const isRemindIntervalInvalid = formDetailsValues.remindInterval === '';

    const tabs = [
        {
            label: 'Create Form +',
            isCreate: true,
            color: '#2b3c4f',
            tabSx: {fontWeight: 700}
        },
        {
            label: 'Active Forms',
            status: [FORM_STATUSES.active],
            color: '#0A8572'
        },
        {
            label: 'Inactive Forms',
            status: [FORM_STATUSES.inactive],
            color: '#ca8a04'
        },
        {
            label: 'Expired Forms',
            status: [FORM_STATUSES.expired],
            color: '#c43829'
        },
        {
            label: 'Archived Forms',
            status: [FORM_STATUSES.archived],
            color: '#64748b'
        },
        {
            label: 'Communications',
            status: [FORM_STATUSES.communication],
            color: '#105090'
        },
        {
            label: 'School Templates',
            isSchoolTemplates: true,
            color: '#2080C0'
        },
    ];

    const TAB_INACTIVE_INDEX = tabs.findIndex(t => t.label.toLowerCase().includes('inactive'));
    const TAB_ACTIVE_INDEX = tabs.findIndex(t =>
        t.status?.includes(FORM_STATUSES.active)
    );
    const TAB_COMMUNICATION_INDEX = tabs.findIndex(t =>
        t.status?.includes(FORM_STATUSES.communication)
    );
    const canArchiveForm = (form) => form && form.status !== FORM_STATUSES.archived;

    const FormsTable = ({
                            statusFilter,
                            outletContext,
                            columns,
                            rowActions,
                            toolbarActions
                        }) => {
        const tableKey = `forms-${(statusFilter ?? []).join('_') || 'all'}`;

        return (
            <Table
                key={tableKey}
                id={`${outletContext?.key || 'forms'}-${tableKey}`}
                columns={columns}
                initialState={{
                    columnFilters: [{
                        id: 'status',
                        value: statusFilter
                    }],
                    excludeFilterIdsFromCount: ['status'],
                }}
                tableProps={{
                    enableSorting: true,
                    manualSorting: true,
                    enableFullScreenToggle: false,
                    enableColumnResizing: false,
                    enableRowSelection: true,
                }}
                rowActions={rowActions}
                toolbarActions={toolbarActions}
                noRowsOverlay={{
                    icon: <ContentPasteOffRoundedIcon/>,
                    text: 'No forms have been added',
                }}
            />
        );
    };

    const makeFormLive = async (selectedRow) => {
        const result = await updateFormStatus({id: selectedRow.id});

        if (result.error) {
            showErrorNotification(result.error.data);
        } else {
            if (selectedRow.isCommunication) {
                showInfoNotification('Communication sent');
                setActiveTabIndex(TAB_COMMUNICATION_INDEX);
            } else {
                showInfoNotification('Form is now live');
                setActiveTabIndex(TAB_ACTIVE_INDEX);
            }
        }
    };

    const editForm = (selectedRow) => {
        navigate(`edit/${selectedRow.id}`, {
            state: {fromTabIndex: activeTabIndex},
        });
    };

    const viewResponses = (selectedRow) => {
        navigate(`responses/${selectedRow.id}`, {
            state: {fromTabIndex: activeTabIndex},
        });
    };

    const hiddenStatusColumnSx = {display: 'none'};

    const columns = useMemo(() => [
        {
            accessorKey: 'name',
            header: 'Name',
            Cell: CustomCell,
            enableSorting: true
        },
        {
            accessorKey: 'assignedGroups',
            header: 'Group',
            Cell: CustomCell,
            enableColumnFilter: false,
            enableSorting: false
        },
        {
            accessorKey: 'accessLevel',
            header: 'Type',
            filterVariant: 'select',
            filterFn: FILTER_OPERATIONS.EQUALS,
            filterSelectOptions: Object.values(FORM_ACCESS_LEVELS).map(levelId => ({
                value: levelId,
                label: FORM_ACCESS_LEVELS_NAMES[levelId]
            })),
            Cell: ({cell}) => FORM_ACCESS_LEVELS_NAMES[cell.getValue()] ?? 'unknown',
            enableSorting: false
        },
        {
            accessorKey: 'status',
            header: 'Status',
            filterVariant: 'multi-select',
            filterFn: FILTER_OPERATIONS.MULTISELECT,
            filterSelectOptions: Object.values(FORM_STATUSES)
                .filter(statusId => statusId !== FORM_STATUSES.deleted)
                .map(statusId => ({
                    value: statusId,
                    label: FORM_STATUSES_NAMES[statusId]
                })),
            // keep status filter working for tab queries, but remove the column from UI
            muiTableHeadCellProps: {sx: hiddenStatusColumnSx},
            muiTableBodyCellProps: {sx: hiddenStatusColumnSx},
            meta: {
                hideFromColumnSelector: true,
            },
            enableSorting: false,
        },
        {
            accessorKey: 'expirationDate',
            header: expirationHeader,
            Cell: ({cell}) => cell.getValue() ? formatFormExpirationDate(cell.getValue()) : '',
            Filter: DateRangePicker,
            filterFn: FILTER_OPERATIONS.BETWEEN_INCLUSIVE,
            enableSorting: false
        },
        {
            accessorKey: 'categories',
            header: 'Category',
            filterVariant: 'multi-select',
            filterFn: FILTER_OPERATIONS.MULTISELECT,
            minSize: 350,
            enableSorting: false,
            filterSelectOptions: formCategories
                ?.map(({
                           id,
                           name
                       }) => ({
                    value: id,
                    label: name
                })) ?? [],
            Cell: ({cell}) => {
                const categories = cell.getValue();

                if (!categories) {
                    return '';
                }

                if (categories.length === 1) {
                    const {
                        color,
                        name
                    } = categories[0];
                    return (
                        <Chip
                            icon={<CircleIcon fontSize="small" style={{color}}/>}
                            label={name}
                            variant="outlined"
                        />
                    );
                }

                return (
                    <Stack flexDirection="row" gap={1} flexWrap="wrap">
                        {categories.map(({
                                             color,
                                             name
                                         }, index) => (
                            <Tooltip key={index} title={name} arrow>
                                <CircleIcon fontSize="small" style={{color}}/>
                            </Tooltip>
                        ))}
                    </Stack>
                );
            },
        },
        {
            accessorKey: 'createdAt',
            header: 'Created At',
            Cell: ({cell}) => formatDate(cell.getValue()),
            Filter: DateRangePicker,
            filterFn: FILTER_OPERATIONS.BETWEEN_INCLUSIVE,
            enableSorting: false
        },
        {
            accessorKey: 'createdBy',
            header: 'Created By',
            enableColumnFilter: false,
            enableSorting: false
        }
    ], [formCategories]);

    const handleDeleteForm = async (form) => {
        try {
            await confirm({
                title: 'Confirm Deletion',
                description: (
                    <>
                        Are you sure you want to delete this form? It will be permanently removed.
                        <br/><br/>
                        <strong>{form.name}</strong>
                    </>
                ),
                confirmationText: 'Confirm',
                cancellationText: 'Cancel',
            });
            await deleteForm(form.id);
            showInfoNotification('Form deleted successfully');
        } catch {

        }
    };

    const handleMakeFormLive = async (selectedRow) => {

        try {
            await confirm({
                title: 'Make Form Live',
                description: (
                    <>
                        Are you sure you want to make this form live? Email notifications will be sent to all assigned
                        recipients, and this action cannot be undone.
                        <br/><br/>
                        <strong>{selectedRow.name}</strong>
                    </>
                ),
                confirmationText: 'Confirm',
                cancellationText: 'Cancel',
            });
            await makeFormLive(selectedRow);

        } catch {
        }
    };

    const handleArchiveForm = async (form) => {
        try {
            await confirm({
                title: 'Confirm Archiving',
                description: (
                    <>
                        Are you sure you want to archive this form? It will no longer be accessible to users.
                        <br/><br/>
                        <strong>{form.name}</strong>
                    </>
                ),
                confirmationText: 'Confirm',
                cancellationText: 'Cancel',
            });
            await archiveForm(form.id);
            showInfoNotification('Form archived successfully');
        } catch {
        }
    };

    const handleCloneForm = async (form) => {
        const result = await cloneForm(form.id);
        if (!result.error) {
            showInfoNotification('Form cloned successfully');
            setActiveTabIndex(TAB_INACTIVE_INDEX);
            return true;
        }

        showErrorNotification('Failed to clone form');
        return false;
    };
    const getDisplayPreviousStatus = (form) => {
        const prev = form?.previousStatus;
        if (!prev) return null;

        if (form?.isCommunication) {
            return FORM_STATUSES_NAMES[FORM_STATUSES.communication] || 'Communication';
        }

        const expiry = form.expirationDate ? new Date(form.expirationDate) : null;
        const now = new Date();

        const isActive = prev.toLowerCase() === 'active';
        const isExpired = expiry && expiry < now;

        if (isActive && isExpired) {
            return FORM_STATUSES_NAMES[FORM_STATUSES.expired] || 'Expired';
        }
        return prev;
    };

    const handleRestoreForm = async (form) => {

        const displayPreviousStatus = getDisplayPreviousStatus(form);

        try {
            await confirm({
                title: 'Confirm Restore',
                description: (
                    <>
                        Are you sure you want to restore this form?{' '}
                        {displayPreviousStatus
                            ? <>It will return to its previous status <strong>{displayPreviousStatus}</strong>.</>
                            : 'It will return to its previous status.'}
                        <br/><br/>
                        <strong>{form.name}</strong>
                    </>
                ),
                confirmationText: 'Confirm',
                cancellationText: 'Cancel',
            });
        } catch {
            return;
        }

        try {
            await restoreForm(form.id).unwrap();
            showInfoNotification('Form restored successfully');
        } catch (err) {
            const message =
                err?.data?.title ||
                err?.data?.detail ||
                err?.data?.message ||
                'Form not restorable';

            showErrorNotification(message);
        }
    };

    const handleMakeTemplate = (selectedRow) => {
        setSelectedFormForTemplate(selectedRow);
        setTemplateDialogOpen(true);
    };

    const handleFormDetailsOpen = (selectedRow) => {
        setSelectedFormForDetails(selectedRow);
        setFormDetailsValues({
            name: selectedRow?.name ?? '',
            categoryIds: selectedRow?.categories?.map(({id}) => id) ?? [],
            expirationDate: selectedRow?.expirationDate ?? null,
            remindInterval: String(selectedRow?.remindInterval ?? 0)
        });
        setFormDetailsDialogOpen(true);
    };

    const handleFormDetailsClose = () => {
        setFormDetailsDialogOpen(false);
        setSelectedFormForDetails(null);
        setFormDetailsValues({
            name: '',
            categoryIds: [],
            expirationDate: null,
            remindInterval: '0'
        });
    };

    const handleSaveFormDetails = async () => {
        if (!selectedFormForDetails || !formNameValidation.isValid || isRemindIntervalInvalid) {
            return;
        }

        const parsedRemindInterval = Number(formDetailsValues.remindInterval || 0);
        const result = await updateFormDetails({
            formId: selectedFormForDetails.id,
            name: formDetailsValues.name.trim(),
            categoryIds: formDetailsValues.categoryIds,
            expirationDate: formDetailsValues.expirationDate,
            remindInterval: Number.isNaN(parsedRemindInterval) ? 0 : parsedRemindInterval
        });

        if (result.error) {
            const error = result.error;
            if (error?.data?.validationErrors) {
                showDetailedErrorNotification('Failed to update form details', error.data.validationErrors);
            } else {
                showErrorNotification(error?.data?.message || 'Failed to update form details');
            }
            return;
        }

        showInfoNotification('Form details updated successfully');
        handleFormDetailsClose();
    };

    const handleTemplateDialogClose = () => {
        setTemplateDialogOpen(false);
        setSelectedFormForTemplate(null);
        templateNameField.reset();
    };

    useEffect(() => {
        if (createFormTemplateResult.isSuccess) {
            showInfoNotification('Template created successfully');
            handleTemplateDialogClose();
        } else if (createFormTemplateResult.isError) {
            const error = createFormTemplateResult.error;
            if (error?.data?.validationErrors) {
                showDetailedErrorNotification('Failed to create template', error.data.validationErrors);
            } else {
                showErrorNotification(error?.data?.message ?? error?.message ?? 'Failed to create template');
            }
        }
    }, [createFormTemplateResult.isSuccess, createFormTemplateResult.isError, createFormTemplateResult.error]);

    useEffect(() => {
        const indexFromState = location.state?.activeTabIndex;

        if (typeof indexFromState === 'number') {
            setActiveTabIndex(indexFromState);
        }
    }, [location.key, location.state?.activeTabIndex]);

    const handleCreateTemplate = async () => {
        if (!templateNameField.value || templateNameField.error) {
            return;
        }

        const trimmedName = templateNameField.value?.trim() ?? '';

        await createFormTemplate({
            formId: selectedFormForTemplate.id,
            name: trimmedName
        });
    };

    const handleEditTemplate = (template) => {
        setSelectedTemplate(template);
        editTemplateNameField.setValue(template.name);
        setEditTemplateDialogOpen(true);
    };

    const handleEditTemplateDialogClose = () => {
        setEditTemplateDialogOpen(false);
        setSelectedTemplate(null);
        editTemplateNameField.reset();
    };

    const handleUpdateTemplate = async () => {
        if (!editTemplateNameField.value || editTemplateNameField.error || !selectedTemplate) {
            return;
        }

        const trimmedName = editTemplateNameField.value?.trim() ?? '';

        const result = await updateSchoolTemplate({
            templateId: selectedTemplate.id,
            name: trimmedName
        });

        if (result.error) {
            const error = result.error;
            if (error?.data?.validationErrors) {
                showDetailedErrorNotification('Failed to update template', error.data.validationErrors);
            } else {
                showErrorNotification(error?.data?.message || 'Failed to update template');
            }
        } else {
            showInfoNotification('Template updated successfully');
            handleEditTemplateDialogClose();
        }
    };

    const handleDeleteTemplate = async (template) => {
        try {
            await confirm({
                title: 'Confirm Deletion',
                description: (
                    <>
                        Are you sure you want to delete this template? It will be permanently removed.
                        <br/><br/>
                        <strong>{template.name}</strong>
                    </>
                ),
                confirmationText: 'Confirm',
                cancellationText: 'Cancel',
            });
            const result = await deleteSchoolTemplate(template.id);
            if (result.error) {
                showErrorNotification(result.error.data?.message || 'Failed to delete template');
            } else {
                showInfoNotification('Template deleted successfully');
            }
        } catch {
        }
    };

    const schoolTemplatesColumns = useMemo(() => [
        {
            accessorKey: 'name',
            header: 'Name',
            Cell: CustomCell,
            enableSorting: false,
            enableColumnFilter: false
        },
        {
            accessorKey: 'type',
            header: 'Type',
            Cell: ({cell}) => {
                const value = cell.getValue();
                if (!value) return '';
                return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
            },
            enableSorting: false,
            enableColumnFilter: false
        }
    ], []);

    const schoolTemplatesRowActions = [
        {
            label: 'Edit Name',
            action: handleEditTemplate
        },
        {
            label: 'Delete',
            action: handleDeleteTemplate
        }
    ];

    /* eslint-enable react/prop-types */

    const handleCloneSelectedForms = async (selectedRows) => {
        if (!selectedRows?.length) {
            return;
        }

        const selectedIds = selectedRows.map((row) => row.id);
        const result = await bulkCloneForms(selectedIds);

        if (result.error) {
            showErrorNotification('Failed to clone forms');
            return;
        }

        showInfoNotification(`${selectedRows.length} forms cloned successfully`);
        setActiveTabIndex(TAB_INACTIVE_INDEX);
    };

    const handleArchiveSelectedForms = async (selectedRows) => {
        if (!selectedRows?.length) {
            return;
        }

        const archivableRows = selectedRows.filter(canArchiveForm);
        if (!archivableRows.length) {
            showErrorNotification('No selected forms can be archived');
            return;
        }

        try {
            await confirm({
                title: 'Confirm Archiving',
                description: (
                    <>
                        Are you sure you want to archive {archivableRows.length} form(s)? They will no longer be
                        accessible to users.
                    </>
                ),
                confirmationText: 'Confirm',
                cancellationText: 'Cancel',
            });
        } catch {
            return;
        }

        const archivableIds = archivableRows.map((row) => row.id);
        const result = await bulkArchiveForms(archivableIds);

        if (result.error) {
            showErrorNotification('Failed to archive forms');
            return;
        }

        showInfoNotification(`${archivableRows.length} forms archived successfully`);
    };

    const formsToolbarActions = [
        {
            type: 'button',
            label: 'Clone',
            renderOnRowSelect: true,
            enableOnRowSelect: true,
            onClick: handleCloneSelectedForms
        },
        {
            type: 'button',
            label: 'Archive',
            renderOnRowSelect: true,
            enableOnRowSelect: true,
            hidden: (selectedRows) => !selectedRows?.some(canArchiveForm),
            onClick: handleArchiveSelectedForms
        }
    ];

    const rowActions = [
        {
            label: 'Form details',
            action: handleFormDetailsOpen,
            hidden: (row) => row?.status === FORM_STATUSES.archived,
        },
        {
            label: 'Edit Form',
            action: editForm,
        },
        {
            disabled: (row) => row && row.status !== FORM_STATUSES.inactive,
            label: 'Make form live',
            action: handleMakeFormLive,
        },
        {
            disabled: (row) => row && row.status === FORM_STATUSES.inactive,
            label: 'View responses',
            action: viewResponses,
        },
        {
            disabled: (row) => row && row.status === FORM_STATUSES.archived,
            label: 'Archive form',
            action: handleArchiveForm,
        },
        {
            disabled: (row) => !row || row.status !== FORM_STATUSES.archived || !row.previousStatus,
            label: 'Restore form',
            action: handleRestoreForm,
        },
        {
            disabled: (row) => row && row.status !== FORM_STATUSES.archived,
            label: 'Delete form',
            action: handleDeleteForm,
        },
        {
            label: 'Clone form',
            action: handleCloneForm,
        },
        {
            label: 'Copy URL',
            action: async (row) => {
                try {
                    await navigator.clipboard.writeText(getFormUrl(row.id));
                    showInfoNotification('Form URL copied to clipboard');
                } catch {
                }
            },
            disabled: (row) => row.accessLevel !== FORM_ACCESS_LEVELS.public,
        },
        {
            label: 'Make template',
            action: handleMakeTemplate
        },
    ];

    return (
        <>
            <AdmicityTabs
                variant="scrollable"
                slots={{panel: {marginTop: 2}}}
                tabs={tabs.map((t) => ({
                    ...t,
                    component: t.isCreate
                        ? <CreateForm/>
                        : t.isSchoolTemplates
                            ? (
                                <SchoolTemplatesTable
                                    id={`${key}-school-templates`}
                                    columns={schoolTemplatesColumns}
                                    outletContext={{key}}
                                    tableProps={{
                                        enableSorting: true,
                                        manualSorting: true,
                                        enableFullScreenToggle: false,
                                        enableColumnResizing: false,
                                        enableHiding: false,
                                        enableFilters: false,
                                        enableTopToolbar: false,
                                    }}
                                    rowActions={schoolTemplatesRowActions}
                                    noRowsOverlay={{
                                        icon: <ContentPasteOffRoundedIcon/>,
                                        text: 'No school templates have been added',
                                    }}
                                />
                            )
                            : (
                                <FormsTable
                                    statusFilter={t.status}
                                    outletContext={{key}}
                                    columns={columns}
                                    rowActions={rowActions}
                                    toolbarActions={formsToolbarActions}
                                />
                            ),
                }))}
                tabIndex={activeTabIndex}
                onChange={(i) => setActiveTabIndex(i)}
            />
            <AdmicityDialog
                open={formDetailsDialogOpen}
                handleClose={handleFormDetailsClose}
                title="Form Details"
                actions={[
                    {
                        label: 'Cancel',
                        onClick: handleFormDetailsClose
                    },
                    {
                        label: 'Save',
                        disabled: !formNameValidation.isValid || isRemindIntervalInvalid,
                        onClick: handleSaveFormDetails
                    }
                ]}
            >
                <Stack spacing={2} sx={{pt: 1}}>
                    <TextField
                        autoFocus
                        required
                        label="Form Name"
                        fullWidth
                        value={formDetailsValues.name}
                        error={!formNameValidation.isValid}
                        helperText={formNameValidation.message || ' '}
                        onChange={(e) => setFormDetailsValues((prev) => ({
                            ...prev,
                            name: e.target.value
                        }))}
                    />
                    <TextField
                        select
                        label="Categories"
                        fullWidth
                        helperText=" "
                        SelectProps={{
                            multiple: true,
                            value: formDetailsValues.categoryIds,
                            renderValue: (selected) => {
                                if (!(formCategories?.length)) {
                                    return <em>No categories</em>;
                                }

                                if (!Array.isArray(selected) || selected.length === 0) {
                                    return <em>No categories</em>;
                                }

                                const selectedCats = formCategories?.filter(c => selected.includes(c.id)) || [];

                                return (
                                    <Stack flexDirection="row" gap={1} flexWrap="wrap">
                                        {selectedCats.map(cat => (
                                            <Box
                                                key={cat.id}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.8,
                                                    padding: 1,
                                                    borderRadius: '18px',
                                                    border: '1px solid #adadad'
                                                }}
                                            >
                                                <CircleIcon
                                                    fontSize="small"
                                                    sx={{color: cat.color}}
                                                />
                                                <Typography variant="body2">
                                                    {cat.name}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                );
                            },
                            MenuProps: {
                                sx: {
                                    '& .MuiMenuItem-root': {
                                        whiteSpace: 'unset',
                                        wordBreak: 'break-all'
                                    }
                                }
                            }
                        }}
                        onChange={(e) => setFormDetailsValues((prev) => ({
                            ...prev,
                            categoryIds: e.target.value
                        }))}
                    >
                        {!formCategories?.length && (
                            <MenuItem disabled>
                                <em>No categories</em>
                            </MenuItem>
                        )}
                        {(formCategories ?? []).map((category) => (
                            <MenuItem key={category.id} value={category.id}>
                                <Checkbox checked={formDetailsValues.categoryIds.includes(category.id)}/>
                                <CircleIcon
                                    fontSize="small"
                                    sx={{
                                        color: category.color,
                                        mr: 1
                                    }}
                                />
                                {category.name}
                            </MenuItem>
                        ))}
                    </TextField>
                    <FormExpirationDatePicker
                        label="Expiration Date (optional)"
                        expirationDate={formDetailsValues.expirationDate}
                        onExpirationDateChange={(expirationDate) =>
                            setFormDetailsValues((prev) => ({
                                ...prev,
                                expirationDate,
                            }))
                        }
                        minDateTime={
                            selectedFormForDetails?.scheduledActivationAt
                                ? moment(selectedFormForDetails.scheduledActivationAt)
                                : moment()
                        }
                    />
                    <TextField
                        label="Remind Interval"
                        fullWidth
                        required
                        value={formDetailsValues.remindInterval}
                        onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '');

                            if (value === '') {
                                setFormDetailsValues((prev) => ({
                                    ...prev,
                                    remindInterval: ''
                                }));
                                return;
                            }

                            const intValue = parseInt(value, 10);
                            if (!Number.isNaN(intValue) && intValue >= 0 && intValue <= 1000) {
                                setFormDetailsValues((prev) => ({
                                    ...prev,
                                    remindInterval: String(intValue)
                                }));
                            }
                        }}
                        error={isRemindIntervalInvalid}
                        helperText={isRemindIntervalInvalid ? 'This field is required.' : 'In hours (0 = Disabled)'}
                        inputProps={{
                            inputMode: 'numeric',
                            pattern: '[0-9]*'
                        }}
                    />
                </Stack>
            </AdmicityDialog>
            <AdmicityDialog
                open={templateDialogOpen}
                handleClose={handleTemplateDialogClose}
                title="Create Template"
                contentText="Enter a name for the template:"
                actions={[
                    {
                        label: 'Cancel',
                        onClick: handleTemplateDialogClose
                    },
                    {
                        label: 'Create',
                        disabled: templateNameField.error || !templateNameField.value,
                        onClick: handleCreateTemplate
                    }
                ]}
            >
                <TextField
                    autoFocus
                    required
                    margin="dense"
                    label="Template Name"
                    fullWidth
                    variant="standard"
                    {...templateNameField}
                />
            </AdmicityDialog>
            <AdmicityDialog
                open={editTemplateDialogOpen}
                handleClose={handleEditTemplateDialogClose}
                title="Edit Template"
                contentText="Update the template name:"
                actions={[
                    {
                        label: 'Cancel',
                        onClick: handleEditTemplateDialogClose
                    },
                    {
                        label: 'Update',
                        disabled: editTemplateNameField.error || !editTemplateNameField.value,
                        onClick: handleUpdateTemplate
                    }
                ]}
            >
                <TextField
                    autoFocus
                    required
                    margin="dense"
                    label="Template Name"
                    fullWidth
                    variant="standard"
                    {...editTemplateNameField}
                />
            </AdmicityDialog>
        </>
    );

};

export default Forms;
